import { useState, type ReactNode } from 'react';
import { ChevronDown } from '@carbon/react/icons';
import { FormattedCount } from 'components/formatted-count';
import styles from './compare-section.module.scss';

export interface CompareSectionProps {
  /** The section's name, on its header bar. */
  label: string;
  /** How many changes the section holds, at the far right of the header bar. */
  count: number;
  /** What the section shows while open: its table of changes. */
  children: ReactNode;
}

/**
 * One section of a workspace Compare sheet: a collapsible grey header bar carrying its
 * change count, over the section's table, as the Compare mocks draw it. Opens expanded.
 *
 * @param {CompareSectionProps} props - The section's label, count and table.
 * @return {JSX.Element} The section.
 */
export function CompareSection({
  label,
  count,
  children,
}: CompareSectionProps): JSX.Element {
  const [open, setOpen] = useState(true);

  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <ChevronDown
          className={open ? styles.chevronOpen : styles.chevron}
          aria-hidden
        />
        <span className={styles.sectionLabel}>{label}</span>
        <span className={styles.sectionCount}>
          <FormattedCount value={count} />
        </span>
      </button>

      {open && children}
    </section>
  );
}
